import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ReservedExecutionType,
  ReservedParticipationType,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import {
  RESERVED_EXECUTION_OPTIONS,
  RESERVED_PARTICIPATION_OPTIONS,
} from "../utils/publicProcurementOptions";
import { useEditor } from "../../../../../common/editing/save-coordinator";

interface Props {
  eventId: string;
}

const SettingsToggle = ({
  label,
  description,
  isChecked,
  isSaving: saving,
  onChange,
}: {
  label: string;
  description: string;
  isChecked: boolean;
  isSaving: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Flex align="center" justify="space-between">
    <Box>
      <Flex align="center" gap="2">
        <Text fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        {saving && <Spinner size="xs" />}
      </Flex>
      <Text fontSize="xs" color="smMuted">
        {description}
      </Text>
    </Box>
    <Switch
      isChecked={isChecked}
      onChange={(e) => onChange(e.target.checked)}
    />
  </Flex>
);

export const AdvancedSettingsSection = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { publicContext } = state;

  const isSaving = (field: string) => {
    const s = fieldStatus(field);
    return s === "saving" || s === "pending";
  };

  return (
    <Accordion allowToggle mb="8">
      <AccordionItem border="1px solid" borderColor="smBorder" borderRadius="lg">
        <AccordionButton py="3" borderRadius="lg">
          <Box flex="1" textAlign="left">
            <Text fontWeight="bold" fontSize="md">
              {t("Advanced settings")}
            </Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb="4">
          <Stack spacing="5">
            <SettingsToggle
              label={t("EU financing")}
              description={t("Is this procurement funded by the EU?")}
              isChecked={publicContext.euFunded ?? false}
              isSaving={isSaving("publicContext.euFunded")}
              onChange={(checked) => updateField({ publicContext: { euFunded: checked } })}
            />
            <SettingsToggle
              label={t("GPA coverage")}
              description={t("Is this procurement covered by the GPA agreement?")}
              isChecked={publicContext.gpaCoverage ?? false}
              isSaving={isSaving("publicContext.gpaCoverage")}
              onChange={(checked) => updateField({ publicContext: { gpaCoverage: checked } })}
            />
            <FormControl>
              <FormLabel fontSize="sm">
                {t("Reserved participation")} {isSaving("publicContext.reservedParticipation") && <Spinner size="xs" />}
              </FormLabel>
              <Select
                size="sm"
                value={publicContext.reservedParticipation ?? ReservedParticipationType.None}
                onChange={(e) =>
                  updateField({
                    publicContext: { reservedParticipation: e.target.value as ReservedParticipationType },
                  })
                }
              >
                {RESERVED_PARTICIPATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.value)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">
                {t("Reserved execution")} {isSaving("publicContext.reservedExecution") && <Spinner size="xs" />}
              </FormLabel>
              <Select
                size="sm"
                value={publicContext.reservedExecution ?? ReservedExecutionType.None}
                onChange={(e) =>
                  updateField({
                    publicContext: { reservedExecution: e.target.value as ReservedExecutionType },
                  })
                }
              >
                {RESERVED_EXECUTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.value)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
